import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MapPin, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { PINCODE } from "@/constants/testIds";

export function PincodeGate({ open, onOpenChange }) {
  const { pincode, setPincode } = useCart();
  const [value, setValue] = useState(pincode || "");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setResult(null);
    if (!/^\d{6}$/.test(value)) {
      setError("Enter a valid 6-digit pincode");
      return;
    }
    setChecking(true);
    try {
      const { data } = await api.get(`/pincode/check`, { params: { pincode: value } });
      setResult(data);
      if (data.serviceable) {
        setPincode(value);
        setTimeout(() => onOpenChange(false), 900);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to check pincode");
    } finally {
      setChecking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={PINCODE.modal} className="bg-hyd-panel border-hyd-border text-hyd-text max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl">
            <MapPin className="w-5 h-5 text-hyd-gold" /> Delivery check
          </DialogTitle>
          <DialogDescription className="text-hyd-text2 text-sm">
            Fireworks delivery is restricted in some regions per Supreme Court orders. Enter your pincode to check.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input
            data-testid={PINCODE.input}
            inputMode="numeric"
            maxLength={6}
            placeholder="6-digit pincode"
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="bg-hyd-bg border-hyd-border text-hyd-text text-lg tracking-widest"
          />
          {error && <div className="text-hyd-red text-sm">{error}</div>}
          {result && (
            <div
              data-testid={PINCODE.result}
              className={`p-3 rounded-lg text-sm flex items-start gap-2 ${
                result.serviceable ? "bg-emerald-950/40 border border-emerald-700 text-emerald-200"
                                    : "bg-red-950/40 border border-red-800 text-red-200"
              }`}
            >
              {result.serviceable ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <XCircle className="w-4 h-4 mt-0.5" />}
              <span>
                {result.serviceable
                  ? `We deliver here! Est. ${result.estimated_days} days.`
                  : result.reason}
              </span>
            </div>
          )}
          <Button
            data-testid={PINCODE.checkBtn}
            type="submit"
            disabled={checking}
            className="w-full bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold"
          >
            {checking ? "Checking..." : "Check availability"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
